import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { ChevronLeft, X, Plus } from "lucide-react";

export default function EditProfile() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  usePageMeta(t("edit_profile"));
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [country, setCountry] = useState(user?.country || "");
  const [bio, setBio] = useState(user?.profile?.bio || "");
  const [contras, setContras] = useState(user?.profile?.contraindications || []);
  const [contraDraft, setContraDraft] = useState("");
  const [weekly, setWeekly] = useState(user?.profile?.weekly_report_enabled !== false);
  const [saving, setSaving] = useState(false);

  const addContra = () => {
    const v = contraDraft.trim();
    if (!v || contras.includes(v)) { setContraDraft(""); return; }
    setContras([...contras, v]); setContraDraft("");
  };

  const save = async () => {
    setSaving(true);
    const patch = {
      name: name.trim(),
      phone: phone.trim(),
      bio: bio.trim(),
      contraindications: contras,
      weekly_report_enabled: weekly,
    };
    const c = country.trim();
    if (c === "" || (c.length === 2 && /^[a-zA-Z]+$/.test(c))) patch.country = c === "" ? null : c.toUpperCase();
    try { await api.patch("/auth/profile", patch); await refreshUser(); toast.success(t("saved")); navigate("/app/account"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children }) => (
    <div className="space-y-1.5">
      <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="mx-auto max-w-xl space-y-5" data-testid="edit-profile-screen">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/app/account")} data-testid="profile-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 hover:bg-accent"><ChevronLeft className="h-5 w-5" /></button>
        <div>
          <h1 className="font-display text-2xl text-foreground">{t("edit_profile")}</h1>
          <p className="text-sm text-muted-foreground">{t("profile_subtitle")}</p>
        </div>
      </div>

      <section className="space-y-4 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("profile_account_section")}</h2>
        <Field label={t("edit_name")}><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" /></Field>
        <Field label={t("email")}><Input value={user?.email || ""} disabled data-testid="profile-email" /></Field>
        <Field label={t("phone")}><Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" /></Field>
        <Field label={t("country")}><Input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} placeholder={t("country_pick_hint")} className="uppercase" data-testid="profile-country" /></Field>
        <Field label={t("bio_label")}><Textarea value={bio} onChange={(e) => setBio(e.target.value)} className="min-h-[80px]" data-testid="profile-bio" /></Field>
      </section>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">{t("profile_health_section")}</h2>
        <Field label={t("contraindications")}>
          <p className="text-xs text-muted-foreground">{t("contraindications_hint")}</p>
          <div className="flex flex-wrap gap-2 pt-1">
            {contras.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded-full bg-secondary/60 px-3 py-1 text-sm text-foreground" data-testid={`contra-${c}`}>
                {c}<button onClick={() => setContras(contras.filter((x) => x !== c))}><X className="h-3.5 w-3.5 text-muted-foreground" /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            <Input value={contraDraft} onChange={(e) => setContraDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addContra())} data-testid="contra-input" />
            <Button variant="outline" onClick={addContra} className="shrink-0 rounded-full" data-testid="contra-add"><Plus className="h-4 w-4" /> {t("add")}</Button>
          </div>
        </Field>
      </section>

      <section className="flex items-center gap-3 rounded-2xl border border-border bg-card p-5">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{t("weekly_report_title")}</p>
          <p className="text-xs text-muted-foreground">{t("weekly_report_hint")}</p>
        </div>
        <Switch checked={weekly} onCheckedChange={setWeekly} data-testid="profile-weekly" />
      </section>

      <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="profile-save">{t("save")}</Button>
    </div>
  );
}
